import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { Trophy, Shuffle, RefreshCw, Medal, Clock, Timer, Award } from "lucide-react";
import { Button } from "../../components/ui/button";

const LiveResultsPage = () => {
  const [playdays, setPlaydays] = useState([]);
  const [selectedPlayday, setSelectedPlayday] = useState("");
  const [draws, setDraws] = useState([]);
  const [results, setResults] = useState([]);
  const [yearEnd, setYearEnd] = useState({});
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    fetchPlaydays();
    fetchYearEnd();
  }, []);

  useEffect(() => {
    if (selectedPlayday) {
      fetchData();
    }
  }, [selectedPlayday]);

  useEffect(() => {
    if (!autoRefresh || !selectedPlayday) return;
    const interval = setInterval(() => {
      fetchData();
    }, 5000); // Refresh every 5 seconds for more real-time feel
    return () => clearInterval(interval);
  }, [autoRefresh, selectedPlayday]);

  const fetchPlaydays = async () => {
    try {
      const response = await axios.get(`${API}/playdays`);
      setPlaydays(response.data);
      if (response.data.length > 0) {
        setSelectedPlayday(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch playdays");
    } finally {
      setLoading(false);
    }
  };

  const fetchYearEnd = async () => {
    try {
      const response = await axios.get(`${API}/results/year-end`);
      setYearEnd(response.data);
    } catch (error) {
      console.error("Failed to fetch year end standings");
    }
  };

  const fetchData = async () => {
    try {
      const [drawsRes, resultsRes] = await Promise.all([
        axios.get(`${API}/public/draws/${selectedPlayday}`),
        axios.get(`${API}/public/results/${selectedPlayday}`)
      ]);
      setDraws(drawsRes.data);
      setResults(resultsRes.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Failed to fetch data");
    }
  };

  const manualRefresh = () => {
    fetchData();
    toast.success("Refreshed!");
  };

  const isRopingEvent = (eventName) => {
    return eventName?.includes("Team Roping") || eventName?.includes("Ribbon Roping");
  };

  const getTimeStatus = (entry) => {
    if (entry.total !== null && entry.total !== undefined) {
      return "completed";
    }
    return "pending";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="live-results-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-text-primary flex items-center gap-2">
            <Trophy className="h-6 w-6 text-accent" />
            Live Results
          </h1>
          <p className="text-text-secondary mt-1">
            Auto-refreshing every 5 seconds
            {lastRefresh && (
              <span className="ml-2 text-xs">
                (Last: {lastRefresh.toLocaleTimeString()})
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-status-success hover:bg-status-success/90" : ""}
          >
            {autoRefresh ? (
              <>
                <span className="animate-pulse mr-2">●</span>
                LIVE
              </>
            ) : (
              "Paused"
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={manualRefresh}
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Playday Selector */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-text-primary">Select Play Day</label>
        <Select value={selectedPlayday} onValueChange={setSelectedPlayday}>
          <SelectTrigger data-testid="playday-select">
            <SelectValue placeholder="Select a play day" />
          </SelectTrigger>
          <SelectContent>
            {playdays.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} ({p.date})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabs for Draws, Results, and Year End */}
      <Tabs defaultValue="draws" className="space-y-4">
        <TabsList className="w-full justify-start bg-secondary/50">
          <TabsTrigger value="draws" className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Timer className="h-4 w-4 mr-2" />
            Draw Order & Times
          </TabsTrigger>
          <TabsTrigger value="results" className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Trophy className="h-4 w-4 mr-2" />
            Final Results
          </TabsTrigger>
          <TabsTrigger value="yearend" className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="yearend-tab">
            <Award className="h-4 w-4 mr-2" />
            Year End Points
          </TabsTrigger>
        </TabsList>

        {/* Draws Tab - Now with Times */}
        <TabsContent value="draws" className="space-y-4">
          {draws.length === 0 ? (
            <Card className="border-primary/20">
              <CardContent className="p-8 text-center">
                <Shuffle className="h-12 w-12 text-text-muted mx-auto mb-3" />
                <p className="text-text-secondary">No draws available yet</p>
              </CardContent>
            </Card>
          ) : (
            draws.map((draw) => {
              const isRoping = isRopingEvent(draw.event_name);
              const completedCount = draw.entries.filter(e => e.total !== null && e.total !== undefined).length;
              const totalCount = draw.entries.length;
              
              return (
                <Card key={draw.id} className="border-primary/20 overflow-hidden">
                  <CardHeader className="py-3 bg-secondary/30">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Timer className="h-5 w-5 text-primary" />
                        {draw.event_name}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          completedCount === totalCount 
                            ? "bg-status-success text-white" 
                            : completedCount > 0 
                              ? "bg-status-warning text-white"
                              : "bg-gray-200 text-gray-600"
                        }`}>
                          {completedCount}/{totalCount} Complete
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/20">
                          <TableHead className="w-16 text-text-primary font-bold">#</TableHead>
                          <TableHead className="text-text-primary font-bold">Contestant</TableHead>
                          {isRoping && (
                            <TableHead className="text-text-primary font-bold">Partner</TableHead>
                          )}
                          <TableHead className="text-right text-text-primary font-bold w-28">Time</TableHead>
                          <TableHead className="text-right text-text-primary font-bold w-24">Penalty</TableHead>
                          <TableHead className="text-right text-text-primary font-bold w-28">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {draw.entries.map((entry) => {
                          const status = getTimeStatus(entry);
                          return (
                            <TableRow 
                              key={entry.id}
                              className={status === "completed" ? "bg-status-success/5" : ""}
                            >
                              <TableCell className="font-heading font-bold text-lg text-primary">
                                {entry.draw_order}
                              </TableCell>
                              <TableCell className="font-medium">{entry.contestant_name}</TableCell>
                              {isRoping && (
                                <TableCell className="text-text-secondary">
                                  {entry.contestant2_name || "-"}
                                </TableCell>
                              )}
                              <TableCell className="text-right font-mono">
                                {entry.time !== null && entry.time !== undefined ? (
                                  <span className="text-text-primary">{entry.time.toFixed(3)}s</span>
                                ) : (
                                  <span className="text-text-muted italic">Waiting...</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {entry.penalty ? (
                                  <span className="text-status-error">+{entry.penalty.toFixed(1)}s</span>
                                ) : entry.time !== null ? (
                                  <span className="text-text-muted">-</span>
                                ) : (
                                  <span className="text-text-muted">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-mono font-bold">
                                {entry.total !== null && entry.total !== undefined ? (
                                  <span className="text-status-success">{entry.total.toFixed(3)}s</span>
                                ) : (
                                  <span className="text-text-muted">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* Results Tab - Sorted by Time */}
        <TabsContent value="results" className="space-y-4">
          {results.length === 0 ? (
            <Card className="border-primary/20">
              <CardContent className="p-8 text-center">
                <Trophy className="h-12 w-12 text-text-muted mx-auto mb-3" />
                <p className="text-text-secondary">No results available yet</p>
              </CardContent>
            </Card>
          ) : (
            results.map((eventResult) => {
              const isRoping = isRopingEvent(eventResult.event_name);
              return (
                <Card key={eventResult.event_id} className="border-primary/20 overflow-hidden">
                  <CardHeader className="py-3 bg-accent/10">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-accent" />
                      {eventResult.event_name}
                      {eventResult.results.length > 0 && (
                        <span className="text-sm font-normal text-text-secondary ml-2">
                          ({eventResult.results.length} contestants)
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/20">
                          <TableHead className="w-16 text-text-primary font-bold">Place</TableHead>
                          <TableHead className="text-text-primary font-bold">Contestant</TableHead>
                          {isRoping && (
                            <TableHead className="text-text-primary font-bold">Partner</TableHead>
                          )}
                          <TableHead className="text-right text-text-primary font-bold">Time</TableHead>
                          <TableHead className="text-right text-text-primary font-bold">Penalty</TableHead>
                          <TableHead className="text-right text-text-primary font-bold">Total</TableHead>
                          <TableHead className="text-right text-text-primary font-bold">Payout</TableHead>
                          <TableHead className="text-right text-text-primary font-bold">Points</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {eventResult.results.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={isRoping ? 8 : 7} className="text-center py-8 text-text-secondary">
                              No times recorded yet
                            </TableCell>
                          </TableRow>
                        ) : (
                          eventResult.results.map((entry, idx) => (
                            <TableRow 
                              key={entry.id} 
                              className={idx < 3 ? 'bg-accent/10' : ''}
                            >
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  {idx < 3 && (
                                    <Medal className={`h-5 w-5 ${
                                      idx === 0 ? 'text-yellow-500' : 
                                      idx === 1 ? 'text-gray-400' : 
                                      'text-orange-400'
                                    }`} />
                                  )}
                                  <span className="font-heading font-bold text-lg">{idx + 1}</span>
                                </div>
                              </TableCell>
                              <TableCell className="font-medium">{entry.contestant_name}</TableCell>
                              {isRoping && (
                                <TableCell className="text-text-secondary">
                                  {entry.contestant2_name || "-"}
                                </TableCell>
                              )}
                              <TableCell className="text-right font-mono">
                                {entry.time?.toFixed(3)}s
                              </TableCell>
                              <TableCell className="text-right font-mono text-status-error">
                                {entry.penalty ? `+${entry.penalty.toFixed(1)}s` : "-"}
                              </TableCell>
                              <TableCell className="text-right font-mono font-bold">
                                {entry.total?.toFixed(3)}s
                              </TableCell>
                              <TableCell className="text-right font-mono font-bold text-status-success">
                                {entry.payout ? `$${entry.payout.toFixed(2)}` : "-"}
                              </TableCell>
                              <TableCell className="text-right font-bold text-accent">
                                {entry.points || "-"}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* Year End Points Tab */}
        <TabsContent value="yearend" className="space-y-4" data-testid="yearend-content">
          {Object.keys(yearEnd).length === 0 ? (
            <Card className="border-primary/20">
              <CardContent className="p-8 text-center">
                <Award className="h-12 w-12 text-text-muted mx-auto mb-3" />
                <p className="text-text-secondary">No year end points available yet</p>
              </CardContent>
            </Card>
          ) : (
            Object.entries(yearEnd)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([ageGroup, contestants]) => (
                <Card key={ageGroup} className="border-primary/20 overflow-hidden">
                  <CardHeader className="py-3 bg-accent/10">
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Award className="h-5 w-5 text-accent" />
                      {ageGroup.charAt(0).toUpperCase() + ageGroup.slice(1)}
                      <span className="text-sm font-normal text-text-secondary ml-2">
                        ({contestants.length} contestants)
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/20">
                          <TableHead className="w-16 text-text-primary font-bold">Rank</TableHead>
                          <TableHead className="text-text-primary font-bold">Contestant</TableHead>
                          <TableHead className="text-right text-text-primary font-bold">Total Points</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contestants.map((contestant, idx) => (
                          <TableRow 
                            key={contestant.contestant_id || idx}
                            className={idx < 3 ? 'bg-accent/10' : ''}
                          >
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {idx < 3 && (
                                  <Medal className={`h-5 w-5 ${
                                    idx === 0 ? 'text-yellow-500' : 
                                    idx === 1 ? 'text-gray-400' : 
                                    'text-orange-400'
                                  }`} />
                                )}
                                <span className="font-heading font-bold text-lg">{idx + 1}</span>
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{contestant.contestant_name}</TableCell>
                            <TableCell className="text-right font-heading font-bold text-lg text-accent">
                              {contestant.total_points}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LiveResultsPage;
